import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import {
    FormGroup,
    FormControlLabel,
    FormControl,
    FormHelperText,
} from 'material-ui/Form';
import Switch from 'material-ui/Switch';
import Typography from 'material-ui/Typography';
import Grid from 'material-ui/Grid';
import Button from 'material-ui/Button';
import NumberFormat from 'react-number-format';
import Input, { InputLabel } from 'material-ui/Input';
import Select from 'material-ui/Select';
import { MenuItem } from 'material-ui/Menu';
import moment from 'moment';
import CurrenyFormat from '../../common/CurrenyFormat';
import ContinueButton from '../../common/components/Buttons/ContinueButton';
import colors from '../../../config/themeColors';

const styles = ({ spacing: { unit } }) => ({
    root: {
        width: 'calc(100% - 32px)',
        marginLeft: '16px',
        marginRight: '16px',
        marginTop: '16px',
    },
    button: {
        marginLeft: unit * 2,
        '&:first-child': {
            marginLeft: 0,
        },
    },
    buttons: {
        marginTop: unit * 2,
        marginBottom: unit * 2,
    },
    formControl: {
        marginTop: unit * 3,
        minWidth: unit * 26,
        '&:not(:last-of-type)': {
            marginRight: unit * 3,
        },
    },
    editDetails: {
        color: colors.brownishGrey,
    },
    infoContainer: {
        padding: '24px',
        textAlign: 'center',
        borderRadius: '4px',
        backgroundColor: colors.white,
        boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.12)',
    },
    image: {
        borderRadius: '50%',
        width: '80px',
        height: '80px',
        margin: '0 auto',
        backgroundColor: colors.white4,
    },
    infoDesc: {
        color: colors.brownishGrey,
        fontSize: '14px',
        margin: '16px',
    },
    quote: {
        fontSize: '20px',
        color: colors.black,
    },
    quoteOrange: {
        color: colors.bloodOrange,
        fontSize: '32px',
    },
});

function ApplicantDetailsSummary(props) {
    const requestTooMuch = props.requestedLoanAmount.amount > props.maxLendAmount.amount;
    const terms = [3, 6, 9].concat(Array.from(Array(35).keys()).map(item => (item + 1) * 12));
    const goToNextStep = () => {
        props.setStepCompleted(5, true);
        props.setStep(6);
    };
    const setStepUncompleted = () => {
        props.setStepCompleted(5, false);
    };
    const setEstaminatedPropertyValue = (event) => {
        props.setEstaminatedPropertyValue(event);
        setStepUncompleted();
    };
    const setEstaminatedPropertyMonthlyRent = (event) => {
        props.setEstaminatedPropertyMonthlyRent(event);
        setStepUncompleted();
    };
    const setRequestedTimeSpan = (event) => {
        props.setRequestedTimeSpan(event);
        setStepUncompleted();
    };
    const setRequestedLoanAmount = (event) => {
        props.setRequestedLoanAmount(event);
        setStepUncompleted();
    };
    const formCompleted = (
        Number(props.estaminatedPropertyValue.amount) > 0
        && Number(props.requestedLoanAmount.amount) > 0
        && Number(props.estaminatedPropertyMonthlyRent.amount) > 0
        && props.requestedTimeSpan
    );
    return (
        <div className={props.classes.root}>
            <Grid
                container
                spacing={16}
            >
                <Grid
                    item
                    xs={12}
                    sm={6}
                >
                    <div>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={props.editData}
                                        onChange={() => {
                                            props.setBoolEditData(!props.editData);
                                        }}
                                    />
                                }
                                classes={{label: props.classes.editDetails}}
                                label="Edit Mortgage Details"
                            />
                        </FormGroup>
                    </div>
                    <FormControl
                        className={props.classes.formControl}
                        disabled={!props.editData}
                    >
                        <InputLabel htmlFor="estaminatedPropertyValue">
                            Estimated property value
                        </InputLabel>
                        <Input
                            id="estaminatedPropertyValue"
                            value={props.estaminatedPropertyValue.amount}
                            onChange={event => setEstaminatedPropertyValue(event)}
                            inputComponent={CurrenyFormat}
                        />
                    </FormControl>
                    <FormControl
                        className={props.classes.formControl}
                        disabled={!props.editData}
                    >
                        <InputLabel htmlFor="estaminatedPropertyMonthlyRent">
                            Estimated monthly rent
                        </InputLabel>
                        <Input
                            id="estaminatedPropertyMonthlyRent"
                            value={props.estaminatedPropertyMonthlyRent.amount}
                            onChange={event => setEstaminatedPropertyMonthlyRent(event)}
                            inputComponent={CurrenyFormat}
                        />
                    </FormControl>
                    <FormControl
                        className={props.classes.formControl}
                        disabled={!props.editData}
                    >
                        <InputLabel htmlFor="requestedTimeSpan">
                            Mortgage term
                        </InputLabel>
                        <Select
                            value={props.requestedTimeSpan}
                            onChange={event => setRequestedTimeSpan(event)}
                            inputProps={{
                                name: 'age',
                                id: 'age-simple',
                            }}
                        >
                            {terms.map(item => (
                                <MenuItem
                                    value={item}
                                    key={item}
                                >
                                    {moment.duration(item, 'months').humanize()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl
                        className={props.classes.formControl}
                        disabled={!props.editData}
                        aria-describedby="requestedLoanAmount-max"
                        error={requestTooMuch}
                    >
                        <InputLabel
                            htmlFor="requestedLoanAmount"
                        >
                            {(
                                props.mortgageType === 'remortgage_current_balance'
                                    ? 'Current loan balance'
                                    : 'Requested loan amount'
                            )}
                        </InputLabel>
                        <Input
                            id="requestedLoanAmount"
                            value={props.requestedLoanAmount.amount}
                            onChange={event => setRequestedLoanAmount(event)}
                            inputComponent={CurrenyFormat}
                        />
                        <FormHelperText id="requestedLoanAmount-max">
                            Max possible:
                            <NumberFormat
                                value={props.maxLendAmount.amount}
                                displayType="text"
                                thousandSeparator
                                prefix="&pound;"
                            />
                        </FormHelperText>
                    </FormControl>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                >
                    <div className={props.classes.infoContainer}>
                        <div className={props.classes.image}>&nbsp;</div>
                        <Typography className={props.classes.infoDesc}>
                            Based on information you provided we may be able to lend you
                        </Typography>
                        <Typography className={props.classes.quote}>
                            up to&nbsp;
                            <NumberFormat
                                className={props.classes.quoteOrange}
                                value={props.maxLendAmount.amount}
                                displayType="text"
                                thousandSeparator
                                prefix="&pound;"
                            />
                        </Typography>
                    </div>
                </Grid>
            </Grid>
            <div className={props.classes.buttons}>
                <Button
                    className={props.classes.button}
                    color="default"
                    onClick={() => props.setStep(4)}
                >
                    Back
                </Button>
                <ContinueButton
                    className={props.classes.button}
                    disabled={requestTooMuch || !formCompleted}
                    onClick={() => {
                        goToNextStep();
                    }}
                >
                    Continue
                </ContinueButton>
            </div>
        </div>
    );
}

ApplicantDetailsSummary.propTypes = {
    setStep: PropTypes.func.isRequired,
    setStepCompleted: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    editData: PropTypes.bool.isRequired,
    setBoolEditData: PropTypes.func.isRequired,
    maxLendAmount: PropTypes.object.isRequired,
    setEstaminatedPropertyValue: PropTypes.func.isRequired,
    setEstaminatedPropertyMonthlyRent: PropTypes.func.isRequired,
    setRequestedLoanAmount: PropTypes.func.isRequired,
    setRequestedTimeSpan: PropTypes.func.isRequired,
    estaminatedPropertyValue: PropTypes.object.isRequired,
    estaminatedPropertyMonthlyRent: PropTypes.object.isRequired,
    requestedLoanAmount: PropTypes.object.isRequired,
    requestedTimeSpan: PropTypes.number.isRequired,
    mortgageType: PropTypes.string.isRequired,
};

export default withStyles(styles)(ApplicantDetailsSummary);



// WEBPACK FOOTER //
// ./src/app/FormStepper/ApplicantDetailsSummary/ApplicantDetailsSummary.js